import { useIntl } from 'react-intl';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsAccessLinkRequest } from '@confluence/external-share-utils/entry-points/useIsAccessLinkRequest.ts';

import type { useIsTwcg2304ImportedPageQuery$data as UseIsImportedPageQueryData } from './__generated__/useIsTwcg2304ImportedPageQuery.graphql.ts';
import { IMPORT_FOLDER_NAMES } from './importFolderNames.ts';

// Show the banner if the page was created within this many milliseconds.
const RECENCY_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

const UseIsImportedPageQuery = graphql`
	query useIsTwcg2304ImportedPageQuery($contentId: ID) {
		singleContent(id: $contentId) {
			subType
			ancestors {
				title
			}
			history {
				createdDate
			}
		}
	}
`;

export type UseIsImportedPageResult = {
	/** True if the page was recently imported via the import flow. */
	isImported: boolean;
	/**
	 * True if the page subtype is 'live', determined from the same network query.
	 * Using this avoids relying on LivePagesStore which may not be updated yet on first SPA navigation.
	 */
	isLivePage: boolean;
};

/**
 * Returns whether the given page appears to have been recently imported:
 * - One of its ancestors has a title matching a known import folder name
 *   (resolved in the current locale — handles translated folder names)
 * - AND the page was created within the last 24 hours
 *
 * Also returns `isLivePage` sourced from the same query to avoid LivePagesStore
 * timing issues on first SPA navigation to a newly imported page.
 */
export const useIsImportedPage = (contentId: string): UseIsImportedPageResult => {
	const { formatMessage } = useIntl();

	// Resolve folder names in the current locale so we match translated titles correctly.
	const importFolderTitles = new Set(
		Object.values(IMPORT_FOLDER_NAMES).map((descriptor) => formatMessage(descriptor)),
	);

	// The `ancestors` expansion is refused for a share token, and an access-link viewer is never
	// mid-onboarding, so the banner this feeds has nothing to show them either way.
	const isAccessLinkViewer = useIsAccessLinkRequest();

	const { data, loading } = useQueryInterop<UseIsImportedPageQueryData>(UseIsImportedPageQuery, {
		variables: { contentId },
		skip: !contentId || isAccessLinkViewer,
	});

	if (isAccessLinkViewer || loading || !data?.singleContent) {
		return { isImported: false, isLivePage: false };
	}

	const { ancestors, history, subType } = data.singleContent;

	const isLivePage = subType === 'live';

	// Check if any ancestor is an import folder (locale-aware comparison)
	const hasImportFolderAncestor = ancestors?.some(
		(ancestor) => ancestor?.title && importFolderTitles.has(ancestor.title),
	);

	if (!hasImportFolderAncestor) {
		return { isImported: false, isLivePage };
	}

	// Check if page was created recently
	const createdDate = history?.createdDate;
	if (!createdDate) {
		return { isImported: false, isLivePage };
	}

	const ageMs = Date.now() - new Date(createdDate).getTime();
	return { isImported: ageMs <= RECENCY_WINDOW_MS, isLivePage };
};
